import { apiFetch } from "@/lib/apiClient";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AdminProfile {
  id: number;
  email: string;
  name: string;
  is_active: boolean;
}

/**
 * Actual wire shape of /admin/login and /admin/refresh (verified against the live
 * backend) — snake_case, and notably includes no admin profile at all. `expires_in_minutes`
 * is in MINUTES, not seconds.
 */
export interface AdminAuthResponse {
  access_token: string;
  token_type: string;
  expires_in_minutes: number;
}

interface AdminApiResult<T> {
  data: T | null;
  error: string | null;
  code: string | null;
}

// ── Core fetch wrapper ────────────────────────────────────────────────────────
// The backend's global exception handler reshapes every error — admin included —
// into { code, message }, same as patient auth. Nothing portal-specific to parse here.

export async function adminRequest<T>(
  path: string,
  options: { method: string; body?: object; protected?: boolean },
): Promise<AdminApiResult<T>> {
  const { data, error } = await apiFetch<T>(path, {
    method: options.method,
    body: options.body ? JSON.stringify(options.body) : undefined,
    portal: "admin",
    skipAuth: !options.protected,
  });

  if (error) return { data: null, error: error.message, code: error.code };
  return { data, error: null, code: null };
}

// ── Auth endpoints ────────────────────────────────────────────────────────────
// Paths and refresh-cookie scope (`medsynciq_admin_refresh`, path `/api/v1/admin`)
// are fixed by the backend contract — login/refresh/logout must stay under that prefix.

/** 401 on bad credentials, 403 if the admin account is disabled. */
export const adminLoginApi = (email: string, password: string) =>
  adminRequest<AdminAuthResponse>("/api/v1/admin/login", { method: "POST", body: { email, password } });

/** Reads the httpOnly refresh cookie server-side and rotates it; no token is passed from JS. */
export const adminRefreshApi = () =>
  adminRequest<AdminAuthResponse>("/api/v1/admin/refresh", { method: "POST" });

/** Revokes the current admin session. */
export const adminLogoutApi = () =>
  adminRequest<{ message?: string }>("/api/v1/admin/logout", { method: "POST", protected: true });
